import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { prepareCustomHeaders } from "../utils/baseQueryHeaders";
import { ProductId } from "../products/product";

// Types
export type EnrichResultId = number;
export type FieldsetId = number;
export type JobId = number;

export enum EnrichStatus {
  NOT_ENRICHED = "Not Enriched",
  ENRICHED = "Enriched",
  ENRICHED_AND_SENT_TO_PIM = "Enriched and sent to PIM",
}

export enum FieldType {
  STRING = "STRING",
  INTEGER = "INTEGER",
  DECIMAL = "DECIMAL",
  BOOLEAN = "BOOLEAN",
  LANGUAGE_STRING = "LANGUAGE_STRING",
  SINGLE_SELECT_LIST = "SINGLE_SELECT_LIST",
  MULTI_SELECT_LIST = "MULTI_SELECT_LIST",
}

export interface Fieldset {
  id: FieldsetId;
  name: string;
}

export interface ProductEnrichViewDataResponse {
  status: EnrichStatus;
  fieldsets: [FieldsetId, string][];
  initial_fieldset_id: number | null;
  web_identification: Record<string, any>;
  image_urls: string[];
  supplier_text: string | null;
  supplier_text_field_id: string | null;
  active_job_id: number | null;
}

export interface EnrichedField {
  key: string;
  display_key: string;
  value: any;
  reasoning: string;
  type: FieldType;
  options: Record<string, string>;
  is_edited: boolean;
}

export interface EnrichResult {
  id: EnrichResultId;
  enriched_fields: EnrichedField[];
  created: string;
  published: string;
  is_edited: boolean;
  job_id: JobId;
}

export interface EnrichFromFieldsetRequest {
  product_id: ProductId;
  fieldset_id: FieldsetId;
  add_reasoning?: boolean;
}

export interface EnrichFromFieldsetResponse {
  job_id: JobId;
}

export interface UpdateEnrichResultFieldRequest {
  product_id: ProductId;
  enrich_result_id: EnrichResultId;
  field_key: string;
  field_value: any;
}

export interface RevertEnrichResultFieldRequest {
  product_id: ProductId;
  enrich_result_id: EnrichResultId;
  field_key: string;
}

export interface UpdateEnrichInputOverrideRequest {
  product_id: ProductId;
  web_identification?: Record<string, any>;
  supplier_text?: string | null;
}

export interface UpdateEnrichInputOverrideResponse {
  web_identification: Record<string, any>;
  supplier_text: string | null;
}

// API definition
export const enrichApi = createApi({
  reducerPath: "enrichApi",
  tagTypes: ["EnrichData", "EnrichResult"],
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/_internal/product",
    prepareHeaders: prepareCustomHeaders,
  }),
  endpoints: (build) => ({
    // Get enrich page data for a product
    getProductEnrichViewData: build.query<
      ProductEnrichViewDataResponse,
      ProductId
    >({
      query: (productId) => `/${productId}/enrich_view_data`,
      providesTags: (result, error, productId) => [
        { type: "EnrichData", id: productId },
      ],
    }),

    // Get latest enrich result for a product
    getLatestEnrichResult: build.query<EnrichResult | null, ProductId>({
      query: (productId) => `/${productId}/latest_enrich_result`,
      providesTags: (result, error, productId) => [
        { type: "EnrichResult", id: productId },
      ],
    }),

    // Trigger enrichment from fieldset
    enrichFromFieldset: build.mutation<
      EnrichFromFieldsetResponse,
      EnrichFromFieldsetRequest
    >({
      query: ({ product_id, ...body }) => ({
        url: `/${product_id}/enrich_from_fieldset`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichResult", id: product_id },
        { type: "EnrichData", id: product_id },
      ],
    }),

    // Update a single field in enrich result
    updateEnrichResultField: build.mutation<
      EnrichResult,
      UpdateEnrichResultFieldRequest
    >({
      query: ({ product_id, enrich_result_id, field_key, field_value }) => ({
        url: `/${product_id}/enrich_result/${enrich_result_id}/field`,
        method: "PATCH",
        body: { field_key, field_value },
      }),
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichResult", id: product_id },
      ],
    }),

    // Revert a single field to original AI value
    revertEnrichResultField: build.mutation<
      EnrichResult,
      RevertEnrichResultFieldRequest
    >({
      query: ({ product_id, enrich_result_id, field_key }) => ({
        url: `/${product_id}/enrich_result/${enrich_result_id}/field/${field_key}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichResult", id: product_id },
      ],
    }),

    updateEnrichInputOverride: build.mutation<
      UpdateEnrichInputOverrideResponse,
      UpdateEnrichInputOverrideRequest
    >({
      query: ({ product_id, ...body }) => ({
        url: `/${product_id}/enrich_input_override`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichData", id: product_id },
      ],
    }),
  }),
});

export const {
  useGetProductEnrichViewDataQuery,
  useGetLatestEnrichResultQuery,
  useEnrichFromFieldsetMutation,
  useUpdateEnrichResultFieldMutation,
  useRevertEnrichResultFieldMutation,
  useUpdateEnrichInputOverrideMutation,
} = enrichApi;
